import React, { useEffect, useId, useRef, useState } from "react";
import classNames from "classnames";
import styles from "./Select.module.scss";
import DownArrowIcon from "../../icons/down-arrow.svg";
import UpArrowIcon from "../../icons/up-arrow.svg";
import OutsideClickHandler from "react-outside-click-handler";
import { Check } from "lucide-react";
import { createPortal } from "react-dom";

const Select = ({
  text,
  field,
  form,
  options,
  value,
  onChange,
  disabled,
  className,
  isOverlay = true,
  variant = "default",
  usePortal = variant === "modal",
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [portalStyle, setPortalStyle] = useState(null);
  const optionsRefs = useRef([]);
  const triggerRef = useRef(null);
  const selectId = useId();

  const handleSelect = (option) => {
    if (field && form) {
      form.setFieldValue(field.name, option.label);
    } else if (onChange) {
      onChange(option.id);
    }
    setIsOpen(false);
  };

  const currentValue = field?.value ?? value;

  // Check if the current field value is an id or label
  const hasValue =
    currentValue !== undefined && currentValue !== null && currentValue !== "";
  const selectedOption = hasValue
    ? options.find(
        (option) => option.label === currentValue || option.id === currentValue,
      )
    : null;

  const hasError =
    field && form && form.errors[field.name] && form.touched[field.name];

  // Scroll to the option starting with the pressed letter
  useEffect(() => {
    if (isOpen) {
      const handleKeyDown = (e) => {
        e.stopPropagation();

        const key = e.key.toUpperCase();
        const matchedIndex = options.findIndex((option) =>
          option.label.toUpperCase().startsWith(key),
        );

        if (matchedIndex !== -1 && optionsRefs.current[matchedIndex]) {
          optionsRefs.current[matchedIndex].scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      };

      window.addEventListener("keydown", handleKeyDown);

      return () => {
        window.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [isOpen, options]);

  const updatePortalPosition = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const margin = 6;
    const maxHeight = 320;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpwards = spaceBelow < maxHeight && spaceAbove > spaceBelow;

    setPortalStyle({
      position: "fixed",
      top: openUpwards ? undefined : rect.bottom + margin,
      bottom: openUpwards ? window.innerHeight - rect.top + margin : undefined,
      left: rect.left,
      width: rect.width,
      maxHeight,
      zIndex: 12000,
    });
  };

  useEffect(() => {
    if (!isOpen || !usePortal) return;
    updatePortalPosition();

    const handleReposition = () => updatePortalPosition();
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [isOpen, usePortal]);

  const optionsNode =
    isOpen && options?.length ? (
      <div
        className={classNames(
          styles.options,
          usePortal ? styles.portalOptions : isOverlay && styles.overlay,
          variant === "modal" && styles.modalVariantOptions,
        )}
        data-select-portal-id={selectId}
        data-ignore-outside-click
        onMouseDown={(e) => e.stopPropagation()}
        style={usePortal ? portalStyle : undefined}
      >
        {options.map((option, index) => {
          const isSelected = hasValue
            ? option.label === currentValue ||
              option.value === currentValue ||
              option.id === currentValue
            : false;

          return (
            <div
              key={index}
              ref={(el) => (optionsRefs.current[index] = el)}
              className={classNames(
                styles.option,
                isSelected ? styles.selected : null,
              )}
              onClick={() => handleSelect(option)}
            >
              <span className={styles.optionCheck}>
                {isSelected && <Check size={16} className={styles.check} />}
              </span>
              <span className={styles.optionLabel}>{option.label}</span>
            </div>
          );
        })}
      </div>
    ) : null;

  return (
    <OutsideClickHandler
      onOutsideClick={(event) => {
        if (
          usePortal &&
          event?.target?.closest?.(`[data-select-portal-id="${selectId}"]`)
        ) {
          return;
        }
        setIsOpen(false);
      }}
    >
      <div
        className={classNames(
          styles.selectWrapper,
          variant === "filters" && styles.filterVariant,
          variant === "modal" && styles.modalVariant,
          className,
        )}
      >
        <div
          className={classNames(
            styles.select,
            disabled && styles.disabled,
            hasError && styles.error,
            selectedOption && styles.active,
          )}
          ref={triggerRef}
          onClick={() => {
            if (disabled) return;
            const next = !isOpen;
            setIsOpen(next);
            if (next && usePortal) {
              requestAnimationFrame(() => updatePortalPosition());
            }
          }}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onKeyDown={(e) => {
            if (disabled) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              const next = !isOpen;
              setIsOpen(next);
              if (next && usePortal) {
                requestAnimationFrame(() => updatePortalPosition());
              }
              return;
            }
            if (e.key === "Escape") {
              setIsOpen(false);
            }
          }}
        >
          {/* Display the label of the selected option or fallback to the placeholder text */}
          <span
            className={classNames({ [styles.placeholder]: !selectedOption })}
          >
            {selectedOption ? selectedOption.label : text}
          </span>{" "}
          {isOpen ? (
            <img
              src={UpArrowIcon}
              alt="Up Arrow Icon"
              className={styles.icon}
            />
          ) : (
            <img
              src={DownArrowIcon}
              alt="Down Arrow Icon"
              className={styles.icon}
            />
          )}
        </div>
        {usePortal && optionsNode
          ? createPortal(optionsNode, document.body)
          : optionsNode}
        {hasError && (
          <div className={styles.errorMessage}>{form.errors[field.name]}</div>
        )}
      </div>
    </OutsideClickHandler>
  );
};

export default Select;
